/**
 * Mirror this prompt's capture geometry to the companion.
 *
 * The webview owns the metadata for every screenshot attached to a prompt,
 * because it is what converts a `screenshot_*` target back to desktop
 * coordinates. The MCP layer needs the same mapping for a different job:
 * noticing that a target was aimed at a downscaled image, and choosing which
 * desktop region to re-capture at full resolution so it can be aimed at
 * properly. It cannot rebuild the mapping on its own - with more than one
 * display it cannot even tell which display `screen=1` was - so it asks the
 * companion, and the companion can only answer if the webview tells it.
 *
 * Best-effort throughout. If this never runs, `get_prompt_screens` answers
 * empty, no refinement happens, and the guide is delivered exactly as before.
 */
import { isTauri } from "@/lib/is-tauri";
import type { ScreenshotScreenMeta } from "@/lib/pointing-transform";

export async function mirrorPromptScreens(
  executionId: string | undefined,
  screens: ScreenshotScreenMeta[],
): Promise<void> {
  if (!executionId || screens.length === 0 || !isTauri()) return;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("register_prompt_screens", { executionId, screens });
  } catch {
    // Nothing here is required for delivery. A companion that predates this
    // command, or a webview that is not the owner, simply does not get the
    // refinement - it must never cost the user their guide.
  }
}
